export const MAX_DIMENSION = 2400;
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;
export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

const WEBP_QUALITY = 0.85;
const ACCEPTED_SOURCE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export interface ConvertedImage {
	blob: Blob;
	width: number;
	height: number;
	filename: string;
}

const scaleToFit = (width: number, height: number) => {
	const longest = Math.max(width, height);
	if (longest <= MAX_DIMENSION) return { width, height };
	const ratio = MAX_DIMENSION / longest;
	return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
};

const loadBitmap = async (file: File): Promise<ImageBitmap> => {
	try {
		return await createImageBitmap(file);
	} catch {
		throw new Error("That file could not be read as an image");
	}
};

const withWebpExtension = (name: string): string =>
	`${name.replace(/\.[^.]+$/, "") || "image"}.webp`;

export const convertToWebp = async (file: File): Promise<ConvertedImage> => {
	if (!ACCEPTED_SOURCE_TYPES.includes(file.type)) {
		throw new Error("Upload a PNG, JPEG, GIF, or WebP image");
	}
	if (file.size > MAX_SOURCE_BYTES) {
		throw new Error(`Choose an image under ${MAX_SOURCE_BYTES / 1024 / 1024}MB`);
	}

	const bitmap = await loadBitmap(file);
	const { width, height } = scaleToFit(bitmap.width, bitmap.height);

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;

	const context = canvas.getContext("2d");
	if (!context) throw new Error("This browser cannot process images");
	context.drawImage(bitmap, 0, 0, width, height);
	bitmap.close();

	const blob = await new Promise<Blob | null>((resolve) =>
		canvas.toBlob(resolve, "image/webp", WEBP_QUALITY),
	);
	if (!blob) throw new Error("Could not convert the image to WebP");
	if (blob.size > MAX_UPLOAD_BYTES) {
		throw new Error(
			`Converted image is ${(blob.size / 1024 / 1024).toFixed(1)}MB, over the ${
				MAX_UPLOAD_BYTES / 1024 / 1024
			}MB limit. Try a smaller or simpler image.`,
		);
	}

	return { blob, width, height, filename: withWebpExtension(file.name) };
};
